import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineCreditCard,
  HiOutlinePuzzlePiece,
  HiOutlineChevronUpDown,
} from "react-icons/hi2";
import { Popover } from "antd";
import { usersApi } from "../../api";
import { appConfig } from "../../app-config";
import { useExtensionDetails, useUser } from "../../hooks";
import { Avatar } from "..";
import { cookie, getTrialPlanDayTillExpired } from "../../util";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { storeGenericActions } from "../../store/slices/generic";
import "./sidebar-user-panel.scss";

export const SidebarUserPanel = (): JSX.Element => {
  const { currentUser, resetUser } = useUser();
  const {
    isInstalled: isExtensionInstalled,
    versionInstalled: extensionVersionInstalled,
  } = useExtensionDetails();
  const [isDetectingExtension, setIsDetectingExtension] =
    useState<boolean>(true);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const isOldExtensionVersion =
    extensionVersionInstalled !== appConfig.actualExtensionVersion;

  useEffect(() => {
    const detectingExtensionTimerId = setTimeout(() => {
      if (typeof isExtensionInstalled === "boolean") {
        setIsDetectingExtension(false);
      }
    }, 1500);

    return () => {
      clearTimeout(detectingExtensionTimerId);
    };
  }, [isExtensionInstalled]);

  useEffect(() => {
    if (isExtensionInstalled !== undefined) {
      dispatch(
        storeGenericActions.update({
          extensionInstalled: isExtensionInstalled,
          oldExtensionVersion: isOldExtensionVersion,
        })
      );
    }
  }, [
    isExtensionInstalled,
    isDetectingExtension,
    extensionVersionInstalled,
    currentUser,
    dispatch,
    isOldExtensionVersion,
  ]);

  const subscriptionTrialPeriodLeft = getTrialPlanDayTillExpired(
    String(currentUser?.trial_expires_at)
  );

  const handleLogout = (): void => {
    setIsOpen(false);
    usersApi
      .fullyLogoutUser()
      .then(() => {
        cookie.remove("__lc_cid");
        cookie.remove("__lc_cst");
        localStorage.clear();
      })
      .catch(() => {})
      .finally(resetUser);
  };

  const handleExtensionStatusClick = (): void => {
    if (!isExtensionInstalled) {
      window.open(appConfig.extensionDownloadLink, "_blank", "noreferrer");
    } else if (isOldExtensionVersion) {
      window.open(appConfig.extensionUpdateLink, "_blank", "noreferrer");
    }
  };

  const billingStatus = currentUser?.billing_status;

  // --- Subscription label & color ---
  const getSubscriptionLabel = (): string => {
    switch (billingStatus) {
      case "past_due": return "Past Due";
      case "canceled": return "Canceled";
      case "expired": return "Expired";
      case "trial": return subscriptionTrialPeriodLeft > 0 ? `Trial (${subscriptionTrialPeriodLeft}d)` : "Expired";
      case "active": return "Active";
      default: return "No plan";
    }
  };

  const getSubscriptionColor = (): string => {
    switch (billingStatus) {
      case "past_due": return "orange";
      case "canceled":
      case "expired": return "red";
      case "trial": return subscriptionTrialPeriodLeft > 1 ? "orange" : "red";
      case "active": return "green";
      default: return "red";
    }
  };

  // --- Extension label & color ---
  const showExtensionInstall = !cookie.get("npn") && !isDetectingExtension && !isExtensionInstalled;
  const showExtensionUpgrade = !isDetectingExtension && isExtensionInstalled && isOldExtensionVersion;

  const getExtensionLabel = (): string => {
    if (isDetectingExtension) return "Detecting...";
    if (showExtensionInstall) return "Not installed";
    if (showExtensionUpgrade) return "Update available";
    return "Connected";
  };

  const getExtensionColor = (): string => {
    if (isDetectingExtension) return "grey";
    if (showExtensionInstall) return "red";
    if (showExtensionUpgrade) return "orange";
    return "green";
  };

  const isExtensionClickable = showExtensionInstall || showExtensionUpgrade;

  const subscriptionColor = getSubscriptionColor();
  const extensionColor = getExtensionColor();

  // --- Priority status for trigger (red > orange > green) ---
  const getTriggerStatus = (): { label: string; color: string } => {
    // Red priorities first
    if (billingStatus === "canceled") return { label: "Canceled", color: "red" };
    if (billingStatus === "expired") return { label: "Expired", color: "red" };
    if (showExtensionInstall) return { label: "Install Extension", color: "red" };
    if (billingStatus === "none") return { label: "No plan", color: "red" };
    if (billingStatus === "trial" && subscriptionTrialPeriodLeft <= 1) return { label: `Trial (${subscriptionTrialPeriodLeft}d)`, color: "red" };
    // Orange priorities
    if (billingStatus === "past_due") return { label: "Past Due", color: "orange" };
    if (showExtensionUpgrade) return { label: "Update Extension", color: "orange" };
    if (billingStatus === "trial") return { label: `Trial (${subscriptionTrialPeriodLeft}d)`, color: "orange" };
    // Green — all good
    return { label: "Active", color: "green" };
  };

  const triggerStatus = getTriggerStatus();

  // --- Display name & initials ---
  const rawName = currentUser?.full_name || currentUser?.email?.split("@")[0] || "";
  const displayName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
  const initials = displayName
    .split(/[\s._-]+/)
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // --- Dropdown content ---
  const dropdownContent = (
    <div className="sidebar-user-panel__menu">
      <div className="sidebar-user-panel__menu-email">{currentUser?.email}</div>

      <div className="sidebar-user-panel__menu-divider" />

      <div className="sidebar-user-panel__menu-item">
        <HiOutlineCreditCard className="sidebar-user-panel__menu-icon" />
        <span className="sidebar-user-panel__menu-text">Subscription</span>
        <span
          className={`sidebar-user-panel__menu-badge sidebar-user-panel__menu-badge--${subscriptionColor}`}
        >
          {getSubscriptionLabel()}
        </span>
      </div>

      <div
        className={`sidebar-user-panel__menu-item ${isExtensionClickable ? "sidebar-user-panel__menu-item--clickable" : ""}`}
        onClick={isExtensionClickable ? handleExtensionStatusClick : undefined}
      >
        <HiOutlinePuzzlePiece className="sidebar-user-panel__menu-icon" />
        <span className="sidebar-user-panel__menu-text">Extension</span>
        <span
          className={`sidebar-user-panel__menu-badge sidebar-user-panel__menu-badge--${extensionColor}`}
        >
          {getExtensionLabel()}
        </span>
      </div>

      <div className="sidebar-user-panel__menu-divider" />

      <div
        className="sidebar-user-panel__menu-item sidebar-user-panel__menu-item--logout"
        onClick={handleLogout}
      >
        <HiOutlineArrowRightOnRectangle className="sidebar-user-panel__menu-icon" />
        <span className="sidebar-user-panel__menu-text">Log out</span>
      </div>
    </div>
  );

  return (
    <div className="sidebar-user-panel">
      <Popover
        content={dropdownContent}
        trigger="click"
        placement="top"
        arrow={false}
        open={isOpen}
        onOpenChange={setIsOpen}
        overlayClassName="sidebar-user-popover"
        overlayStyle={{ minWidth: triggerRef.current?.offsetWidth, maxWidth: 300 }}
        align={{ offset: [0, 0] }}
      >
        <div className="sidebar-user-panel__trigger" ref={triggerRef}>
          {currentUser?.picture ? (
            <Avatar
              imageSrc={currentUser.picture}
              imageAlt={displayName}
            />
          ) : (
            <span className="sidebar-user-panel__initials">{initials}</span>
          )}
          <div className="sidebar-user-panel__trigger-info">
            <span className="sidebar-user-panel__trigger-name">
              {displayName}
            </span>
            <span className={`sidebar-user-panel__trigger-plan sidebar-user-panel__trigger-plan--${triggerStatus.color}`}>
              {triggerStatus.label}
            </span>
          </div>
          <HiOutlineChevronUpDown className="sidebar-user-panel__trigger-chevron" />
        </div>
      </Popover>
    </div>
  );
};
